import { useMutation } from "@apollo/client";
import { DSPalette } from "@clickbank-ui/seller-design-system";
import { Close } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Typography
} from "@mui/material";
import { Stack } from "@mui/system";
import { useContext, useState } from "react";
import { Trans } from "react-i18next";

import { ApolloClient, CANCEL_PENDING_ORDER } from "../../../Api";
import { AppContext } from "../../../context/AppContext";

const { palette, cbNeutral } = DSPalette;

const styles = {
	dialog: {
		".MuiDialogTitle-root": {
			textAlign: "center",
			pb: 3
		},
		".MuiDialogContent-root": {
			textAlign: "center"
		},
		".MuiDialogActions-root": {
			pt: 2,
			gap: 1
		}
	}
};

const getStateStyles = state => {
	switch (state) {
		case "TOO_MANY_ATTEMPTS":
		case "PRICE_TOO_LOW":
		case "CANCELED_BY_USER":
		case "ERROR":
			return {
				bar: {
					backgroundColor: palette.error.light
				},
				text: {
					label: <Trans i18nKey="OrderDetails.pending.Canceled">Order Canceled</Trans>,
					color: palette.error.dark
				}
			};
		case "PENDING_RETRY":
		case "RETRYING":
		case "SUCCESS":
		default:
			return {
				bar: {
					border: `1px solid ${cbNeutral[300]}`
				},
				text: {
					label: <Trans i18nKey="OrderDetails.pending.Pending">Order Pending</Trans>,
					color: cbNeutral[300]
				}
			};
	}
};

export const PendingOrderState = ({ receipt, pendingOrderState }) => {
	const { setAlertText, showAlert, setAlert, setActionSuccessful, refetchOrderByReceiptNo } =
		useContext(AppContext);

	const [cancelOrder, { loading }] = useMutation(CANCEL_PENDING_ORDER, {
		client: ApolloClient,
		onError: () => {
			setAlertText(
				<Trans i18nKey="Common.error.unknown">
					An unknown error has occurred. Please contact customer support directly.
				</Trans>
			);
			setAlert(true);
			setActionSuccessful(false);
		},
		onCompleted: () => {
			refetchOrderByReceiptNo(receipt);
			!showAlert && setAlert(true);
			setAlertText(
				<Trans i18nKey="OrderSummary.orderPendingCancelled">
					Your order was canceled successfully
				</Trans>
			);
			setActionSuccessful(true);
		}
	});

	const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false);

	if (!pendingOrderState) {
		return null;
	}

	const {
		text: { label, ...text },
		bar
	} = getStateStyles(pendingOrderState);

	return (
		<>
			<Box
				display="flex"
				alignItems="center"
				justifyContent="center"
				mt={{ xs: "24px" }}
				sx={{
					height: 48,
					padding: "0 1rem",
					borderRadius: "4px",
					...bar
				}}
			>
				<Typography
					sx={{
						fontSize: "17px",
						fontWeight: "700",
						...text
					}}
				>
					{label}
				</Typography>
			</Box>
			{(pendingOrderState === "PENDING_RETRY" || pendingOrderState === "RETRYING") && (
				<LoadingButton
					fullWidth
					variant="outlined"
					color="warning"
					onClick={() => setIsCancelConfirmOpen(true)}
					disabled={loading}
					loading={loading}
				>
					<Typography
						sx={{
							fontSize: "17px",
							fontWeight: "800",
							color: palette.sunshine[200]
						}}
					>
						<Trans i18nKey="OrderDetails.cancelOrder">Cancel Order</Trans>
					</Typography>
				</LoadingButton>
			)}

			<Dialog
				open={isCancelConfirmOpen}
				onClose={() => setIsCancelConfirmOpen(false)}
				maxWidth="sm"
				aria-labelledby="cancel-order-confirmation-dialog"
				aria-describedby="cancel-order-confirmation-description"
				sx={styles.dialog}
			>
				<DialogTitle id="cancel-order-confirmation-dialog" variant="h4">
					<Stack direction="row">
						<Box as="span" flex={1}>
							<Trans i18nKey="OrderDetails.pending.cancelConfirmTitle">
								Are you sure you want to cancel your order?
							</Trans>
						</Box>
						<IconButton
							aria-label="close"
							onClick={() => setIsCancelConfirmOpen(false)}
							sx={{ padding: 0, color: cbNeutral[700] }}
						>
							<Close />
						</IconButton>
					</Stack>
				</DialogTitle>

				<DialogContent id="cancel-order-confirmation-description">
					<Trans i18nKey="OrderDetails.pending.cancelConfirmMessage">
						You cannot undo this action.
					</Trans>
				</DialogContent>

				<DialogActions>
					<Button
						fullWidth
						onClick={() => setIsCancelConfirmOpen(false)}
						disabled={loading}
					>
						<Trans i18nKey="OrderDetails.pending.neverMind">Never Mind</Trans>
					</Button>
					<LoadingButton
						color="error"
						variant="outlined"
						fullWidth
						sx={{ border: "none" }}
						onClick={() => {
							setIsCancelConfirmOpen(false);
							cancelOrder({ variables: { receipt } });
						}}
						loading={loading}
						disabled={loading}
					>
						<Trans i18nKey="OrderDetails.cancelOrder">Cancel Order</Trans>
					</LoadingButton>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default PendingOrderState;
